import { create } from 'zustand'
import { useAuthStore } from './authStore'
import { generatePlantSnapshot } from '../utils/herbarium'

export const useGameStore = create((set, get) => ({
  // Game configuration
  config: null,

  // Player data
  inventory: [],
  seedInventory: { default: 1 }, // Graines par type: { plantType: quantity }
  ownedBackgrounds: ['background_garden'], // Le jardin par défaut est gratuit
  selectedBackground: 'background_garden',
  ownedTools: [],
  herbarium: {},
  lastFreeSeedClaim: null, // Timestamp de la dernière récupération de graine gratuite
  freeSeedCooldown: 13 * 60 * 60 * 1000, // 13 heures en millisecondes

  // Plants (multiple plants support)
  plants: [], // Array of { id, type, config, plantedAt, position: {x, y}, progress, stage, ... }
  selectedPlantId: null, // ID of the currently selected plant

  // Legacy support (kept for compatibility)
  currentPlant: null,
  plantProgress: 0,
  plantStage: 0,

  // Seed generation
  pendingSeed: false,
  seedProgress: 0,
  seedClickBonus: 0, // Bonus from clicks

  // Water system
  waterLevel: 100, // 0 to 100
  lastWaterRefill: Date.now(),
  waterRefillInterval: 13 * 60 * 60 * 1000, // 13 heures
  waterCooldownUntil: null,

  // Subscription
  subscription: null,

  // Auto gardener tool
  autoGardenerOwned: false,
  autoGardenerLastTick: null,
  autoGardenerInterval: 10000, // legacy - kept for backward compatibility
  autoGardenerConfig: {
    clicksPerTick: 1,
    intervalMs: 10000,
  },
  
  // Auto-save state
  _lastSave: 0,
  _saveTimeout: null,

  // Tool effects
  fertilizerMultiplier: 1,
  fertilizerActiveUntil: null,
  wateringCanMultiplier: 1,

  // Actions
  loadGameConfig: async () => {
    try {
      const response = await fetch('/config/game-config.json')
      const config = await response.json()
      set({ config })
    } catch (error) {
      console.error('Failed to load game config:', error)
    }
  },

  plantSeed: (seedType = 'default') => {
    const { config, seedInventory } = get()
    if (!config) return

    // Vérifier si on a cette graine
    if (!seedInventory[seedType] || seedInventory[seedType] <= 0) {
      return false
    }

    const plantConfig = config.plants[seedType]
    const newSeedInventory = { ...seedInventory }
    newSeedInventory[seedType] -= 1

    // Supprimer la clé si la quantité est 0
    if (newSeedInventory[seedType] === 0) {
      delete newSeedInventory[seedType]
    }

    set({
      currentPlant: {
        type: seedType,
        config: plantConfig,
        plantedAt: Date.now(),
        branches: [],
        lastSeedGeneration: Date.now(),
      },
      plantProgress: 0,
      plantStage: 0,
      seedInventory: newSeedInventory,
    })

    return true
  },

  unlockTool: (toolId, toolConfig = {}) => {
    if (!toolId) return false

    if (toolId === 'fertilizer') {
      get().activateFertilizerEffect(toolConfig)
      return true
    }

    if (toolId === 'watering_can_upgrade') {
      const multiplier = toolConfig.wateringMultiplier || 2
      set((state) => {
        if (state.ownedTools.includes(toolId)) {
          return {
            wateringCanMultiplier: multiplier,
          }
        }
        return {
          ownedTools: [...state.ownedTools, toolId],
          wateringCanMultiplier: multiplier,
        }
      })
      return true
    }

    if (toolId === 'pruning_shears') {
      set((state) => {
        if (state.ownedTools.includes(toolId)) return state
        return { ownedTools: [...state.ownedTools, toolId] }
      })
      return true
    }

    // Default: store in ownedTools
    set((state) => {
      if (state.ownedTools.includes(toolId)) return state
      return { ownedTools: [...state.ownedTools, toolId] }
    })
    return true
  },

  checkFertilizerEffect: () => {
    const { fertilizerActiveUntil, fertilizerMultiplier } = get()
    if (!fertilizerActiveUntil) return
    if (Date.now() >= fertilizerActiveUntil) {
      if (fertilizerMultiplier !== 1) {
        set({ fertilizerMultiplier: 1, fertilizerActiveUntil: null })
      } else {
        set({ fertilizerActiveUntil: null })
      }
    }
  },

  plantSeedAt: (seedType, position) => {
    const { config, seedInventory, plants } = get()

    if (!config) return false

    // Vérifier si on a cette graine
    if (!seedInventory[seedType] || seedInventory[seedType] <= 0) {
      return false
    }

    const plantConfig = config.plants[seedType]
    const newSeedInventory = { ...seedInventory }
    newSeedInventory[seedType] -= 1

    // Supprimer la clé si la quantité est 0
    if (newSeedInventory[seedType] === 0) {
      delete newSeedInventory[seedType]
    }

    // Créer une nouvelle plante
    const newPlant = {
      id: `plant_${Date.now()}_${Math.random()}`,
      type: seedType,
      config: plantConfig,
      plantedAt: Date.now(),
      position: position, // { x, y }
      progress: 0,
      stage: 0,
      branches: [],
      lastSeedGeneration: Date.now(),
      pendingSeed: false,
      seedProgress: 0,
      seedClickBonus: 0,
    }

    set({
      plants: [...plants, newPlant],
      selectedPlantId: newPlant.id,
      seedInventory: newSeedInventory,
    })

    return true
  },

  addProgressToPlant: (plantId, amount) => {
    const { plants, fertilizerMultiplier } = get()
    const plantIndex = plants.findIndex(p => p.id === plantId)
    if (plantIndex === -1) return

    const plant = plants[plantIndex]
    const totalClicks = plant.config.clicksPerStage * plant.config.stages
    const isMature = plant.progress >= totalClicks

    const updatedPlants = [...plants]
    const effectiveAmount = amount * (fertilizerMultiplier || 1)
    
    if (isMature) {
      // Si la plante est mature, ajouter un bonus à la production de graines
      const currentBonus = plant.seedClickBonus || 0
      updatedPlants[plantIndex] = {
        ...plant,
        seedClickBonus: currentBonus + effectiveAmount
      }
      get().registerHerbariumEntry(updatedPlants[plantIndex])
    } else {
      // Sinon, faire pousser la plante normalement
      const newProgress = Math.min(plant.progress + effectiveAmount, totalClicks)
      const newStage = Math.floor(newProgress / plant.config.clicksPerStage)

      updatedPlants[plantIndex] = {
        ...plant,
        progress: newProgress,
        stage: newStage,
      }

      if (newProgress >= totalClicks) {
        get().registerHerbariumEntry(updatedPlants[plantIndex])
      }
    }

    set({ plants: updatedPlants })
  },

  unlockAutoGardener: (configOverrides = {}) => {
    const currentConfig = get().autoGardenerConfig || {}

    const baseConfig = {
      clicksPerTick: 1,
      intervalMs: 10000,
    }

    const mergedConfig = {
      ...baseConfig,
      ...currentConfig,
      ...configOverrides,
    }

    if (get().autoGardenerOwned) {
      set({
        autoGardenerConfig: mergedConfig,
        autoGardenerInterval: mergedConfig.intervalMs,
      })
      return true
    }

    set({
      autoGardenerOwned: true,
      autoGardenerLastTick: Date.now(),
      autoGardenerConfig: mergedConfig,
      autoGardenerInterval: mergedConfig.intervalMs,
    })
    return true
  },

  autoCollectReadySeeds: () => {
    const { plants } = get()
    if (!plants || plants.length === 0) return

    plants.forEach((plant) => {
      if (plant?.pendingSeed) {
        get().collectSeedFromPlant(plant.id)
      }
    })
  },

  runAutoGardenerTick: ({ force = false } = {}) => {
    const state = get()
    const {
      autoGardenerOwned,
      autoGardenerLastTick,
      autoGardenerInterval,
      autoGardenerConfig,
      plants,
    } = state

    if (!autoGardenerOwned || !Array.isArray(plants) || plants.length === 0) {
      return
    }

    const now = Date.now()
    const interval = autoGardenerConfig?.intervalMs || autoGardenerInterval || 10000
    const clicksPerTick = autoGardenerConfig?.clicksPerTick || 1
    const lastTick = autoGardenerLastTick ?? now
    const elapsed = Math.max(0, now - lastTick)
    const ticksElapsed = Math.floor(elapsed / interval)
    const hasPendingSeeds = plants.some((plant) => plant?.pendingSeed)

    if (!force && ticksElapsed === 0 && !hasPendingSeeds) {
      return
    }

    const totalClicks = ticksElapsed * clicksPerTick

    if (totalClicks > 0) {
      const currentPlants = get().plants
      currentPlants.forEach((plant) => {
        if (plant?.id) {
          get().addProgressToPlant(plant.id, totalClicks)
        }
      })
    }

    if (ticksElapsed > 0) {
      const remainder = elapsed % interval
      set({
        autoGardenerLastTick: now - remainder,
      })
    } else if (autoGardenerLastTick === null) {
      set({ autoGardenerLastTick: now })
    }

    state.updateAllPlantsSeedProgress()
    state.autoCollectReadySeeds()
  },

  updateAllPlantsSeedProgress: () => {
    const { plants } = get()
    const now = Date.now()

    const updatedPlants = plants.map(plant => {
      const totalClicks = plant.config.clicksPerStage * plant.config.stages
      const isMature = plant.progress >= totalClicks

      if (!isMature) return plant

      const timeSinceLastSeed = now - (plant.lastSeedGeneration || plant.plantedAt)
      const interval = plant.config.seedGenerationInterval || 300000 // 5 min par défaut
      const baseProgress = (timeSinceLastSeed / interval) * 100
      const totalProgress = Math.min(baseProgress + (plant.seedClickBonus || 0), 100)

      const pendingSeed = totalProgress >= 100

      return {
        ...plant,
        seedProgress: totalProgress,
        pendingSeed
      }
    })

    set({ plants: updatedPlants })
  },

  collectSeedFromPlant: (plantId) => {
    const { plants, seedInventory } = get()
    const plantIndex = plants.findIndex(p => p.id === plantId)
    if (plantIndex === -1) return

    const plant = plants[plantIndex]
    if (!plant.pendingSeed) return

    const newSeedInventory = { ...seedInventory }
    newSeedInventory[plant.type] = (newSeedInventory[plant.type] || 0) + 1

    const updatedPlants = [...plants]
    updatedPlants[plantIndex] = {
      ...plant,
      lastSeedGeneration: Date.now(),
      seedProgress: 0,
      pendingSeed: false,
      seedClickBonus: 0
    }

    set({
      plants: updatedPlants,
      seedInventory: newSeedInventory
    })
  },

  addSeeds: (amount = 1, seedType = 'default') => {
    if (amount <= 0) return
    set((state) => {
      const newSeedInventory = { ...state.seedInventory }
      newSeedInventory[seedType] = (newSeedInventory[seedType] || 0) + amount
      return { seedInventory: newSeedInventory }
    })
  },

  registerHerbariumEntry: (plant, { snapshot = null } = {}) => {
    if (!plant || !plant.type) return

    const plantType = plant.type
    const state = get()
    const existingEntry = state.herbarium?.[plantType]
    const alreadyRegistered = Boolean(existingEntry)
    const needsSnapshot = !snapshot && (!existingEntry || !existingEntry.snapshot)

    if (alreadyRegistered && !needsSnapshot) {
      return
    }

    set((draft) => {
      const currentEntry = draft.herbarium?.[plantType]
      if (currentEntry && !needsSnapshot) {
        return draft
      }

      return {
        herbarium: {
          ...(draft.herbarium || {}),
          [plantType]: {
            discoveredAt: currentEntry?.discoveredAt || Date.now(),
            snapshot: snapshot || currentEntry?.snapshot || null,
          },
        },
      }
    })

    if (needsSnapshot && typeof window !== 'undefined') {
      const config = state.config
      // Générer une miniature asynchrone sans bloquer le rendu principal
      Promise.resolve(generatePlantSnapshot(plant, config))
        .then((image) => {
          if (!image) return
          set((draft) => {
            const entry = draft.herbarium?.[plantType]
            if (!entry || entry.snapshot === image) {
              return draft
            }

            return {
              herbarium: {
                ...draft.herbarium,
                [plantType]: {
                  ...entry,
                  snapshot: image,
                },
              },
            }
          })
        })
        .catch((error) => {
          console.warn('Failed to generate herbarium snapshot:', error)
        })
    }
  },

  setHerbarium: (entries = {}) => {
    set({ herbarium: entries || {} })
  },

  syncHerbariumFromPlants: () => {
    const state = get()
    if (!Array.isArray(state.plants) || state.plants.length === 0) return

    state.plants.forEach((plant) => {
      if (!plant?.type || !plant?.config) return
      const totalClicks = (plant.config.clicksPerStage || 0) * (plant.config.stages || 0)
      const hasProgress = (plant.progress || 0) > 0
      const isMature = totalClicks > 0 && (plant.progress || 0) >= totalClicks
      if (hasProgress || isMature) {
        get().registerHerbariumEntry(plant)
      }
    })
  },

  // Débiter un certain nombre de graines en tant que "monnaie" générique
  spendSeeds: (amount) => {
    if (amount <= 0) return true
    const { seedInventory } = get()

    const currentDefault = seedInventory.default || 0
    if (currentDefault < amount) {
      return false
    }

    const newSeedInventory = { ...seedInventory }
    newSeedInventory.default = currentDefault - amount
    if (newSeedInventory.default === 0) {
      delete newSeedInventory.default
    }

    set({ seedInventory: newSeedInventory })
    return true
  },

  destroyPlantById: (plantId) => {
    const { plants, selectedPlantId, seedInventory } = get()

    const plantToDestroy = plants.find((p) => p.id === plantId)
    const updatedPlants = plants.filter((p) => p.id !== plantId)

    // Si la plante détruite était sélectionnée, désélectionner
    const newSelectedPlantId = selectedPlantId === plantId ? null : selectedPlantId

    const newState = {
      plants: updatedPlants,
      selectedPlantId: newSelectedPlantId,
    }

    // Sacrifice : donner 2 graines du même type uniquement si la plante est mature
    if (plantToDestroy && plantToDestroy.type && plantToDestroy.config) {
      const totalClicks = plantToDestroy.config.clicksPerStage * plantToDestroy.config.stages
      const isMature = (plantToDestroy.progress || 0) >= totalClicks

      if (isMature) {
        const newSeedInventory = { ...seedInventory }
        newSeedInventory[plantToDestroy.type] = (newSeedInventory[plantToDestroy.type] || 0) + 2
        newState.seedInventory = newSeedInventory
      }
    }

    set(newState)
  },

  clearGardenAfterPublish: () => {
    get().setState({
      plants: [],
      selectedPlantId: null,
      currentPlant: null,
      plantProgress: 0,
      plantStage: 0,
      pendingSeed: false,
      seedProgress: 0,
      seedClickBonus: 0,
    })
  },

  prunePlantBranches: (plantId, branchKey) => {
    if (!branchKey) return

    const { plants } = get()
    const updatedPlants = plants.map((plant) => {
      if (plant.id !== plantId) return plant

      const prunedBranches = Array.isArray(plant.prunedBranches)
        ? [...plant.prunedBranches]
        : []

      if (prunedBranches.includes(branchKey)) return plant

      prunedBranches.push(branchKey)

      return {
        ...plant,
        prunedBranches,
      }
    })
    set({ plants: updatedPlants })
  },

  canClaimFreeSeed: () => {
    const { lastFreeSeedClaim, freeSeedCooldown } = get()
    if (!lastFreeSeedClaim) return true

    const now = Date.now()
    const timeSinceLastClaim = now - lastFreeSeedClaim
    return timeSinceLastClaim >= freeSeedCooldown
  },

  getFreeSeedTimeRemaining: () => {
    const { lastFreeSeedClaim, freeSeedCooldown } = get()
    if (!lastFreeSeedClaim) return 0

    const now = Date.now()
    const timeSinceLastClaim = now - lastFreeSeedClaim
    const remaining = freeSeedCooldown - timeSinceLastClaim
    return Math.max(0, remaining)
  },

  claimFreeSeed: () => {
    const { canClaimFreeSeed, seedInventory } = get()

    if (!canClaimFreeSeed()) {
      return false
    }

    const newSeedInventory = { ...seedInventory }
    newSeedInventory.default = (newSeedInventory.default || 0) + 1

    set({
      seedInventory: newSeedInventory,
      lastFreeSeedClaim: Date.now()
    })

    return true
  },

  addProgress: (amount) => {
    const { currentPlant, plantProgress, plantStage, seedProgress, seedClickBonus = 0, fertilizerMultiplier } = get()
    if (!currentPlant) return

    const totalClicks = currentPlant.config.clicksPerStage * currentPlant.config.stages
    const isMature = plantProgress >= totalClicks
    const effectiveAmount = amount * (fertilizerMultiplier || 1)

    if (isMature) {
      // Plant is mature, contribute to seed production instead
      // Track click bonus separately from time-based progress
      const newClickBonus = seedClickBonus + effectiveAmount
      set({ seedClickBonus: newClickBonus })

      // Total progress is time-based + click bonus
      const now = Date.now()
      const timeSinceLastGen = now - currentPlant.lastSeedGeneration
      const interval = currentPlant.config.seedGenerationInterval
      const timeProgress = (timeSinceLastGen / interval) * 100
      const totalProgress = Math.min(100, timeProgress + newClickBonus)

      set({ seedProgress: totalProgress })

      // If seed is ready, mark it as pending
      if (totalProgress >= 100) {
        set({ pendingSeed: true })
      }
    } else {
      // Plant is still growing
      const newProgress = plantProgress + effectiveAmount
      const clicksPerStage = currentPlant.config.clicksPerStage
      const newStage = Math.floor(newProgress / clicksPerStage)

      set({
        plantProgress: newProgress,
        plantStage: Math.min(newStage, currentPlant.config.stages - 1),
      })
    }
  },

  waterPlant: () => {
    const { waterLevel, waterCooldownUntil } = get()
    if (waterCooldownUntil && Date.now() < waterCooldownUntil) return false
    if (waterLevel < 10) return false

    const newLevel = Math.max(0, waterLevel - 10)
    const updates = { waterLevel: newLevel }

    if (newLevel === 0) {
      updates.waterCooldownUntil = Date.now() + get().waterRefillInterval
      updates.lastWaterRefill = Date.now()
    }

    set(updates)
    return true
  },

  refillWater: () => {
    const { waterCooldownUntil } = get()
    if (!waterCooldownUntil) return false
    const now = Date.now()
    if (now >= waterCooldownUntil) {
      set({ waterLevel: 100, waterCooldownUntil: null, lastWaterRefill: now })
      return true
    }
    return false
  },

  checkWaterRefill: () => {
    const { waterCooldownUntil } = get()
    if (!waterCooldownUntil) return
    get().refillWater()
  },

  hydrateWaterCooldown: () => {
    const { waterCooldownUntil, waterLevel, lastWaterRefill, waterRefillInterval } = get()

    // Si on a déjà un cooldown actif ou que l'eau n'est pas vide, ne rien faire
    if (waterCooldownUntil || waterLevel > 0) return

    // Cas normal : on reconstruit la fin du cooldown à partir du dernier refill
    if (lastWaterRefill && !Number.isNaN(lastWaterRefill)) {
      const expectedCooldownEnd = lastWaterRefill + waterRefillInterval
      if (Date.now() < expectedCooldownEnd) {
        set({ waterCooldownUntil: expectedCooldownEnd })
        return
      }
    }

    // Cas legacy / incohérent : eau à 0 mais pas de timestamps valides
    // On démarre un nouveau cooldown à partir de maintenant
    const newCooldownEnd = Date.now() + waterRefillInterval
    set({
      waterCooldownUntil: newCooldownEnd,
      lastWaterRefill: Date.now(),
    })
  },

  getWaterCooldownRemaining: () => {
    const { waterCooldownUntil } = get()
    if (!waterCooldownUntil) return 0
    return Math.max(0, waterCooldownUntil - Date.now())
  },

  clickSun: () => {
    const { currentPlant } = get()
    if (!currentPlant) return

    const value = currentPlant.config.sunClickValue
    get().addProgress(value)
  },

  updateSeedProgress: () => {
    const { currentPlant, plantProgress, pendingSeed, seedClickBonus = 0 } = get()
    if (!currentPlant || pendingSeed) return

    // Check if plant is mature based on total clicks
    const totalClicks = currentPlant.config.clicksPerStage * currentPlant.config.stages
    const isMature = plantProgress >= totalClicks
    if (!isMature) return

    const now = Date.now()
    const timeSinceLastGen = now - currentPlant.lastSeedGeneration
    const interval = currentPlant.config.seedGenerationInterval

    // Calculate time-based progress
    const timeProgress = (timeSinceLastGen / interval) * 100

    // Add click bonus to time progress
    const totalProgress = Math.min(100, timeProgress + seedClickBonus)

    if (totalProgress >= 100) {
      set({ pendingSeed: true, seedProgress: 100 })
    } else {
      set({ seedProgress: totalProgress })
    }
  },

  collectSeed: () => {
    const { pendingSeed, currentPlant, seedInventory } = get()
    if (!pendingSeed || !currentPlant) return

    const newSeedInventory = { ...seedInventory }
    newSeedInventory[currentPlant.type] = (newSeedInventory[currentPlant.type] || 0) + 1

    set({
      seedInventory: newSeedInventory,
      pendingSeed: false,
      seedProgress: 0,
      seedClickBonus: 0,
    })
  },

  addToInventory: (item) => {
    set((state) => {
      // Si c'est une graine, l'ajouter au seedInventory
      if (item.plantType) {
        const newSeedInventory = { ...state.seedInventory }
        newSeedInventory[item.plantType] = (newSeedInventory[item.plantType] || 0) + 1
        return { seedInventory: newSeedInventory }
      }
      // Sinon, ajouter à l'inventaire normal
      return {
        inventory: [...state.inventory, { ...item, acquiredAt: Date.now() }],
      }
    })
  },

  removeFromInventory: (itemId) => {
    set((state) => ({
      inventory: state.inventory.filter((item) => item.id !== itemId),
    }))
  },

  destroyPlant: () => {
    set({
      currentPlant: null,
      plantProgress: 0,
      plantStage: 0,
    })
  },

  setSubscription: (subscription) => {
    set({ subscription })
  },

  pruneBranch: (branchIndex) => {
    set((state) => {
      if (!state.currentPlant) return state

      const branches = [...state.currentPlant.branches]
      branches.splice(branchIndex, 1)

      return {
        currentPlant: {
          ...state.currentPlant,
          branches,
        },
      }
    })
  },

  selectBackground: (backgroundId) => {
    const { ownedBackgrounds } = get()
    if (ownedBackgrounds.includes(backgroundId)) {
      set({ selectedBackground: backgroundId })
      return true
    }
    return false
  },

  resetAnonymousGarden: () => {
    // Réinitialiser l'état du jardin pour un utilisateur anonyme
    set({
      inventory: [],
      seedInventory: { default: 1 },
      plants: [],
      selectedPlantId: null,
      currentPlant: null,
      plantProgress: 0,
      plantStage: 0,
      waterLevel: 100,
      lastWaterRefill: Date.now(),
      waterCooldownUntil: null,
      pendingSeed: false,
      seedProgress: 0,
      seedClickBonus: 0,
      autoGardenerOwned: false,
      autoGardenerLastTick: null,
      ownedBackgrounds: ['background_garden'],
      selectedBackground: 'background_garden',
      ownedTools: [],
      herbarium: {},
      lastFreeSeedClaim: null,
      subscription: null,
      wateringCanMultiplier: 1,
    }, false) // Ne pas sauvegarder lors de la réinitialisation
  },

  // Save game state to Supabase
  saveGame: async () => {
    const { saveUserData } = useAuthStore.getState()
    const now = Date.now()
    
    // Limiter la fréquence des sauvegardes à une fois toutes les 2 secondes
    if (now - get()._lastSave < 2000) {
      return { saved: false, error: 'Trop de sauvegardes en peu de temps' }
    }
    
    try {
      const { error } = await saveUserData()
      if (error) throw error
      
      set({ _lastSave: now })
      return { saved: true, error: null }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error)
      return { saved: false, error }
    }
  },

  // Set state with auto-save
  setState: (partial, save = true) => {
    set(partial)
    if (save) {
      // Annuler la sauvegarde précédente si elle est en attente
      if (get()._saveTimeout) {
        clearTimeout(get()._saveTimeout)
      }
      
      // Planifier une nouvelle sauvegarde après un délai
      const timeout = setTimeout(() => {
        get().saveGame()
      }, 2000) // 2 secondes de délai
      
      set({ _saveTimeout: timeout })
    }
  }
}))

let backgroundLoopId = null

const startBackgroundLoop = () => {
  if (typeof window === 'undefined') return
  if (backgroundLoopId) return

  const TICK_MS = 1000

  backgroundLoopId = window.setInterval(() => {
    const state = useGameStore.getState()
    if (!state) return

    if (typeof state.updateAllPlantsSeedProgress === 'function') {
      state.updateAllPlantsSeedProgress()
    }

    if (state.autoGardenerOwned && typeof state.runAutoGardenerTick === 'function') {
      state.runAutoGardenerTick()
    }

    if (typeof state.checkFertilizerEffect === 'function') {
      state.checkFertilizerEffect()
    }
  }, TICK_MS)

  // Premier tick immédiat pour rattraper le temps écoulé
  const state = useGameStore.getState()
  if (state?.autoGardenerOwned && typeof state.runAutoGardenerTick === 'function') {
    state.runAutoGardenerTick({ force: true })
  }

  if (typeof state?.checkFertilizerEffect === 'function') {
    state.checkFertilizerEffect()
  }
}

startBackgroundLoop()
